import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DirectionsRenderer, GoogleMap, Marker } from "@react-google-maps/api";
import axios from "axios";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { getSocket } from "../../initSocket";
import { useGoogleMaps } from "../DeliveryMap/useGoogleMaps";
import {
  reverseGeocodeLatLng,
  vehicleLabel,
  formatLocationAge,
  createVehicleMarkerIcon,
} from "../../../components/DriverAdmin/driverMapUtils";

type LatLng = { lat: number; lng: number };

interface Props {
  tripId: string;
  pickup: LatLng;
  dropoff: LatLng;
  pickupAddress?: string;
  dropoffAddress?: string;
  status?: string;
}

type LiveData = {
  status?: string;
  driver?: { id?: string; name?: string; vehicleType?: string } | null;
  location?: {
    lat?: number;
    lng?: number;
    speed?: number;
    heading?: number;
    accuracy?: number;
    text?: string;
  } | null;
  locationAgeSeconds?: number | null;
  locationFresh?: boolean;
  locationSource?: string | null;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const POLL_INTERVAL_MS = 4000;
const clean = (value: unknown) => String(value || "").trim();
const validPoint = (value: any) =>
  Number.isFinite(Number(value?.lat)) && Number.isFinite(Number(value?.lng));

const prettifyStatus = (value: unknown) =>
  clean(value || "Waiting for driver")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function LiveDriverMap({
  tripId,
  pickup,
  dropoff,
  pickupAddress,
  dropoffAddress,
  status,
}: Props) {
  const { isLoaded, loadError } = useGoogleMaps();
  const socket = getSocket();

  const mapRef = useRef<google.maps.Map | null>(null);
  const animationRef = useRef<number | null>(null);
  const currentPositionRef = useRef<LatLng | null>(null);
  const resolvedEndpointRef = useRef("");
  const mountedRef = useRef(true);

  const [driverPos, setDriverPos] = useState<LatLng | null>(null);
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [liveData, setLiveData] = useState<LiveData | null>(null);
  const [driverAddress, setDriverAddress] = useState("");
  const [trackingError, setTrackingError] = useState("");
  const [loadingLive, setLoadingLive] = useState(true);

  const mapHeight = useBreakpointValue({
    base: "340px",
    md: "450px",
    lg: "550px",
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    const service = new google.maps.DirectionsService();
    service.route(
      {
        origin: pickup,
        destination: dropoff,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, routeStatus) => {
        if (!mountedRef.current) return;
        setDirections(routeStatus === "OK" && result ? result : null);
      },
    );
  }, [isLoaded, pickup.lat, pickup.lng, dropoff.lat, dropoff.lng]);

  const animateTo = useCallback((end: LatLng) => {
    if (!validPoint(end)) return;

    const start = currentPositionRef.current;
    if (!start) {
      currentPositionRef.current = end;
      setDriverPos(end);
      return;
    }

    const duration = 1800;
    const startTime = performance.now();

    const step = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const next = {
        lat: start.lat + (end.lat - start.lat) * progress,
        lng: start.lng + (end.lng - start.lng) * progress,
      };

      if (mountedRef.current) setDriverPos(next);
      currentPositionRef.current = next;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      }
    };

    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    animationRef.current = requestAnimationFrame(step);
  }, []);

  const applyLivePayload = useCallback(
    (data: LiveData) => {
      if (!mountedRef.current) return;
      setLiveData((previous) => ({ ...(previous || {}), ...(data || {}) }));
      setTrackingError("");

      if (validPoint(data?.location)) {
        const point = {
          lat: Number(data.location!.lat),
          lng: Number(data.location!.lng),
        };
        animateTo(point);

        const suppliedAddress = clean(data.location?.text);
        if (suppliedAddress) {
          setDriverAddress(suppliedAddress);
        } else if (isLoaded) {
          void reverseGeocodeLatLng(point).then((address) => {
            if (mountedRef.current && address) setDriverAddress(address);
          });
        }
      }
    },
    [animateTo, isLoaded],
  );

  const loadPersistedLocation = useCallback(
    async (silent = false) => {
      if (!tripId) return;
      if (!silent) setLoadingLive(true);

      const known = resolvedEndpointRef.current;
      const endpoints = known
        ? [known]
        : [
            `${API_BASE}/api/v1/dispatch/live/source/delivery/${encodeURIComponent(tripId)}`,
            `${API_BASE}/api/v1/dispatch/live/source/order/${encodeURIComponent(tripId)}`,
            `${API_BASE}/api/v1/dispatch/jobs/${encodeURIComponent(tripId)}/live`,
          ];

      let lastError: any = null;
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint, { timeout: 10000 });
          const data = (response.data?.data || response.data || {}) as LiveData;
          resolvedEndpointRef.current = endpoint;
          applyLivePayload(data);
          if (!silent && mountedRef.current) setLoadingLive(false);
          return;
        } catch (error: any) {
          lastError = error;
          if (known && error?.response?.status === 404) {
            resolvedEndpointRef.current = "";
          }
        }
      }

      if (mountedRef.current) {
        setTrackingError(
          Number(lastError?.response?.status) === 404
            ? "Waiting for an assigned driver."
            : lastError?.response?.data?.message ||
                lastError?.message ||
                "Live location is temporarily unavailable.",
        );
        if (!silent) setLoadingLive(false);
      }
    },
    [applyLivePayload, tripId],
  );

  useEffect(() => {
    resolvedEndpointRef.current = "";
    currentPositionRef.current = null;
    setDriverPos(null);
    setLiveData(null);
    setDriverAddress("");
    void loadPersistedLocation(false);

    const timer = window.setInterval(() => {
      void loadPersistedLocation(true);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [loadPersistedLocation, tripId]);

  useEffect(() => {
    socket.emit("joinTripRoom", { tripId });

    const locationHandler = (data: any) => {
      if (!validPoint(data)) return;
      applyLivePayload({
        location: {
          lat: Number(data.lat),
          lng: Number(data.lng),
          speed: Number(data.speed || 0),
          heading: Number(data.heading || 0),
          accuracy: data.accuracy,
          text: data.text,
        },
      });
    };

    const statusHandler = (data: any) => {
      const nextStatus = clean(data?.status || data);
      if (nextStatus) {
        setLiveData((previous) => ({
          ...(previous || {}),
          status: nextStatus,
        }));
      }
    };

    socket.on("locationUpdate", locationHandler);
    socket.on("driverLocationUpdate", locationHandler);
    socket.on("statusUpdate", statusHandler);

    return () => {
      socket.off("locationUpdate", locationHandler);
      socket.off("driverLocationUpdate", locationHandler);
      socket.off("statusUpdate", statusHandler);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [applyLivePayload, socket, tripId]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(pickup);
    bounds.extend(dropoff);
    if (driverPos) bounds.extend(driverPos);
    mapRef.current.fitBounds(bounds, 70);

    const listener = google.maps.event.addListenerOnce(
      mapRef.current,
      "bounds_changed",
      () => {
        if ((mapRef.current?.getZoom() || 0) > 16) mapRef.current?.setZoom(16);
      },
    );

    return () => google.maps.event.removeListener(listener);
  }, [
    isLoaded,
    pickup.lat,
    pickup.lng,
    dropoff.lat,
    dropoff.lng,
    driverPos?.lat,
    driverPos?.lng,
  ]);

  const markerHeading = useMemo(() => {
    const rawHeading = liveData?.location?.heading;
    const heading = Number(rawHeading);
    return rawHeading != null && Number.isFinite(heading) ? heading : 0;
  }, [liveData?.location?.heading]);

  const displayStatus = prettifyStatus(liveData?.status || status);
  const driverName = clean(liveData?.driver?.name) || "Assigned driver";
  const vehicle = vehicleLabel(liveData?.driver?.vehicleType);

  const cardsBorderRadii = "8px";

  if (loadError) return <Box color="red.500">Maps failed to load.</Box>;
  if (!isLoaded) return <Box p={6}>Loading map…</Box>;

  return (
    <Box
      bg="white"
      borderRadius={cardsBorderRadii}
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      w="100%"
    >
      <Flex
        bg="white"
        color="gray.800"
        px={{ base: 3, md: 4 }}
        py={3}
        align={{ base: "flex-start", md: "center" }}
        justify="space-between"
        direction={{ base: "column", md: "row" }}
        gap={2}
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Box>
          <Text fontSize="md" fontWeight="800">
            Live tracking
          </Text>
          <Text fontSize="xs" color="gray.600">
            {liveData?.driver
              ? `${driverName} • ${vehicle}`
              : "Waiting for driver GPS"}
          </Text>
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme="blue">{displayStatus}</Badge>
          <Badge
            colorScheme={
              liveData?.locationFresh
                ? "green"
                : liveData?.location
                  ? "orange"
                  : "gray"
            }
          >
            {liveData?.locationFresh
              ? "Live GPS"
              : liveData?.location
                ? formatLocationAge(liveData?.locationAgeSeconds)
                : "No GPS yet"}
          </Badge>
        </HStack>
      </Flex>

      {driverAddress || trackingError ? (
        <VStack align="stretch" spacing={1} px={{ base: 3, md: 4 }} py={2}>
          {driverAddress ? (
            <Text fontSize="sm" color="gray.700">
              Driver near {driverAddress}
            </Text>
          ) : null}
          {trackingError && !driverPos ? (
            <Text fontSize="sm" color="orange.600">
              {trackingError}
            </Text>
          ) : null}
        </VStack>
      ) : null}

      <Box height={mapHeight} w="100%" position="relative">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={driverPos || pickup}
          zoom={14}
          options={{
            zoomControl: true,
            scrollwheel: true,
            fullscreenControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            clickableIcons: false,
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
        >
          {directions ? (
            <DirectionsRenderer
              directions={directions}
              options={{ suppressMarkers: true, preserveViewport: true }}
            />
          ) : null}

          <Marker
            position={pickup}
            title={pickupAddress || "Pickup"}
            label={{ text: "P", color: "white", fontWeight: "700" }}
          />
          <Marker
            position={dropoff}
            title={dropoffAddress || "Drop-off"}
            label={{ text: "D", color: "white", fontWeight: "700" }}
          />

          {driverPos ? (
            <Marker
              position={driverPos}
              title={`${driverName} • ${vehicle}`}
              icon={createVehicleMarkerIcon(
                liveData?.driver?.vehicleType,
                markerHeading,
              )}
              zIndex={999}
            />
          ) : null}
        </GoogleMap>

        {loadingLive ? (
          <Flex
            position="absolute"
            top={3}
            right={3}
            bg="whiteAlpha.900"
            px={3}
            py={2}
            borderRadius={cardsBorderRadii}
            gap={2}
            align="center"
            boxShadow="sm"
          >
            <Spinner size="sm" />
            <Text fontSize="xs">Loading live GPS…</Text>
          </Flex>
        ) : null}
      </Box>
    </Box>
  );
}
